import type { CasinoGame } from '@/domain';
import { cn } from '@/lib/cn';
import { EmptyState, Spinner } from '@/ui/common';
import { GameTile } from './GameTile';

export interface GameGridProps {
  games: CasinoGame[];
  title?: string;
  isLoading?: boolean;
  isFetchingMore?: boolean;
  categoryOverride?: string;
  showFavorite?: boolean;
  onToggleFavorite?: (game: CasinoGame) => void;
  sentinelRef?: (node: HTMLDivElement | null) => void;
  className?: string;
}

export function GameGrid({
  games,
  title,
  isLoading = false,
  isFetchingMore = false,
  categoryOverride,
  showFavorite,
  onToggleFavorite,
  sentinelRef,
  className,
}: GameGridProps) {
  if (isLoading && games.length === 0) {
    return (
      <div className={cn('flex min-h-64 items-center justify-center', className)}>
        <Spinner label="Loading games" />
      </div>
    );
  }

  if (!isLoading && games.length === 0) {
    return (
      <EmptyState
        icon="casino"
        title="No games found"
        description="Try another category, provider, or search."
        className={className}
      />
    );
  }

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {title ? <h2 className="text-heading-s text-text-primary">{title}</h2> : null}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7">
        {games.map((game) => (
          <GameTile
            key={`${game.ProviderGameId}-${game.GameTag}`}
            game={game}
            categoryOverride={categoryOverride}
            showFavorite={showFavorite}
            onToggleFavorite={onToggleFavorite}
            size="sm"
          />
        ))}
      </div>
      <div ref={sentinelRef} className="h-8 w-full" aria-hidden />
      {isFetchingMore ? (
        <div className="flex justify-center py-4">
          <Spinner size="sm" label="Loading more" />
        </div>
      ) : null}
    </div>
  );
}
